import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Switch,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Textarea,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
} from '@mui/joy';
import {
  Calendar as CalendarIcon,
  Clock as ScheduleIcon,
  LinkSimple as LinkIcon,
  Copy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  GoogleLogo as GoogleIcon,
  Trash as DeleteIcon,
  ArrowsClockwise as RefreshIcon,
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { schedulingApi } from '../../lib/apiScheduling';
import type {
  AvailabilitySettings,
  WorkingHours,
} from '../../types/scheduling';
import { getDefaultWorkingHours } from '../../types/scheduling';
import { PageHeader } from '../../components/common/PageHeader';

const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS: Record<string, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

export const SchedulingSettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'danger' | 'warning'; message: string } | null>(null);
  const [embedCodeModalOpen, setEmbedCodeModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const queryClient = useQueryClient();

  // Local state for settings form
  const [workingHours, setWorkingHours] = useState<WorkingHours>(getDefaultWorkingHours());
  const [meetingDuration, setMeetingDuration] = useState(30);
  const [bookingWindow, setBookingWindow] = useState(30);
  const [minimumNotice, setMinimumNotice] = useState(0);
  const [customSlug, setCustomSlug] = useState('');
  const [meetingTitle, setMeetingTitle] = useState('Meeting');
  const [meetingLocation, setMeetingLocation] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [timezone, setTimezone] = useState('America/New_York');

  // Check for OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const error = params.get('error');

    if (success === 'calendar_connected' || success === 'calendar_updated') {
      const message = { type: 'success' as const, message: 'Successfully connected your calendar!' };
      setAlertMessage(message);
      window.history.replaceState({}, document.title, window.location.pathname);
      void queryClient.invalidateQueries({ queryKey: ['calendarConnections'] });
    } else if (error) {
      const message = { type: 'danger' as const, message: `Failed to connect calendar: ${error.replace(/_/g, ' ')}` };
      setAlertMessage(message);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Fetch calendar connections
  const { data: calendarConnections = [], isLoading: connectionsLoading, isError: connectionsError, refetch: refetchConnections } = useQuery({
    queryKey: ['calendarConnections'],
    queryFn: schedulingApi.getCalendarConnections,
  });

  // Fetch availability settings
  const { data: settings, isLoading: settingsLoading, isError: settingsError, refetch: refetchSettings } = useQuery({
    queryKey: ['availabilitySettings'],
    queryFn: schedulingApi.getAvailabilitySettings,
  });

  // Load settings into form when fetched
  useEffect(() => {
    if (settings) {
      setWorkingHours(settings.working_hours || getDefaultWorkingHours());
      setMeetingDuration(settings.default_meeting_duration);
      setBookingWindow(settings.booking_window_days);
      setMinimumNotice(settings.minimum_notice_hours);
      setCustomSlug(settings.custom_slug || '');
      setMeetingTitle(settings.meeting_title);
      setMeetingLocation(settings.meeting_location || '');
      setWelcomeMessage(settings.welcome_message || '');
      setIsEnabled(settings.is_enabled);
      setTimezone(settings.time_zone);
    }
  }, [settings]);

  // Connect calendar mutation
  const connectCalendarMutation = useMutation({
    mutationFn: (provider: 'google' | 'outlook') => schedulingApi.connectCalendar(provider),
    onSuccess: (data) => {
      if (data.connected) {
        void queryClient.invalidateQueries({ queryKey: ['calendarConnections'] });
        setAlertMessage({ type: 'success', message: `Calendar connected using your profile account${data.email ? ` (${data.email})` : ''}.` });
      } else if (data.oauth_url) {
        window.location.href = data.oauth_url;
      }
    },
    onError: () => {
      setAlertMessage({
        type: 'danger',
        message: 'Failed to initiate calendar connection'
      });
    },
  });

  // Disconnect calendar mutation
  const disconnectCalendarMutation = useMutation({
    mutationFn: (connectionId: string) => schedulingApi.disconnectCalendar(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendarConnections'] });
      setAlertMessage({
        type: 'success',
        message: 'Calendar disconnected successfully'
      });
    },
    onError: () => {
      setAlertMessage({
        type: 'danger',
        message: 'Failed to disconnect calendar'
      });
    },
  });

  // Sync calendar mutation
  const syncCalendarMutation = useMutation({
    mutationFn: (connectionId: string) => schedulingApi.syncCalendar(connectionId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['calendarConnections'] });
      setAlertMessage({
        type: 'success',
        message: `Sync successful! Found ${data.calendars?.length || 0} calendars.`
      });
    },
    onError: () => {
      setAlertMessage({
        type: 'danger',
        message: 'Failed to sync calendar'
      });
    },
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: (data: Partial<AvailabilitySettings>) => schedulingApi.updateAvailabilitySettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availabilitySettings'] });
      setAlertMessage({
        type: 'success',
        message: 'Settings updated successfully'
      });
    },
    onError: (error: unknown) => {
      console.error('Failed to update settings:', error);
      const err = error as { response?: { data?: { detail?: string } }; message?: string };
      setAlertMessage({
        type: 'danger',
        message: err?.response?.data?.detail || err?.message || 'Failed to update settings'
      });
    },
  });

  // Preview availability query
  const { data: previewData, refetch: refetchPreview, isLoading: previewLoading, error: previewError } = useQuery({
    queryKey: ['availabilityPreview'],
    queryFn: schedulingApi.previewAvailability,
    enabled: false, // Only fetch when user clicks preview button
    retry: false, // Don't retry on error
  });

  const handleSaveSettings = () => {
    updateSettingsMutation.mutate({
      working_hours: workingHours,
      time_zone: timezone,
      default_meeting_duration: meetingDuration,
      booking_window_days: bookingWindow,
      minimum_notice_hours: minimumNotice,
      custom_slug: customSlug,
      meeting_title: meetingTitle,
      meeting_location: meetingLocation,
      welcome_message: welcomeMessage,
      is_enabled: isEnabled,
    });
  };

  const handlePreviewAvailability = () => {
    refetchPreview();
    setPreviewModalOpen(true);
  };

  const handleToggleDay = (day: string) => {
    setWorkingHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        enabled: !prev[day].enabled,
      }
    }));
  };

  const handleTimeChange = (day: string, field: 'start' | 'end', value: string) => {
    setWorkingHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      }
    }));
  };

  const handleCopyBookingLink = async () => {
    if (!settings?.booking_url) return;
    try {
      await navigator.clipboard.writeText(settings.booking_url);
      setAlertMessage({ type: 'success', message: 'Booking link copied to clipboard!' });
    } catch {
      setAlertMessage({ type: 'danger', message: 'Could not copy your booking link. Please try again.' });
    }
  };

  if (connectionsError || settingsError) return <Alert color="danger" endDecorator={<Button variant="soft" color="danger" onClick={() => { refetchConnections(); refetchSettings(); }}>Retry</Button>}>Could not load scheduling settings.</Alert>;

  if (connectionsLoading || settingsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader title="Scheduling" description="Set your availability and booking preferences, and connect a calendar to avoid conflicts." />

      {alertMessage && (
        <Alert
          color={alertMessage.type}
          sx={{ mb: 3 }}
          endDecorator={
            <IconButton size="sm" onClick={() => setAlertMessage(null)}>
              ✕
            </IconButton>
          }
        >
          {alertMessage.message}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value as number)}>
        <TabList sx={{ flexWrap: 'wrap', '& > *': { flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' } } }}>
          <Tab>
            <CalendarIcon style={{ marginRight: 8 }} />
            Calendar Connection
          </Tab>
          <Tab>
            <ScheduleIcon style={{ marginRight: 8 }} />
            Availability
          </Tab>
          <Tab>
            <LinkIcon style={{ marginRight: 8 }} />
            Booking Page
          </Tab>
        </TabList>

        {/* Tab 1: Calendar Connection */}
        <TabPanel value={0}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 2 }}>
                Connect Your Calendar
              </Typography>
              <Typography level="body-sm" sx={{ mb: 3 }}>
                ApexOS booking works with your native schedule. Optionally connect Google or Outlook to include outside appointments. Outlook reuses your profile mailbox; Microsoft may request calendar consent once.
              </Typography>

              {calendarConnections.length === 0 ? (
                <Stack spacing={2}>
                  <Alert color="warning" startDecorator={<WarningIcon />}>
                    No external calendar connected. You can configure availability and enable your ApexOS booking page now.
                  </Alert>
                  <Box>
                    <Button
                      startDecorator={<GoogleIcon />}
                      onClick={() => connectCalendarMutation.mutate('google')}
                      loading={connectCalendarMutation.isPending}
                      sx={{ mr: 2 }}
                    >
                      Connect Google Calendar
                    </Button>
                    <Button
                      startDecorator={<CalendarIcon />}
                      onClick={() => connectCalendarMutation.mutate('outlook')}
                      loading={connectCalendarMutation.isPending}
                      variant="outlined"
                      color="primary"
                    >
                      Connect Outlook
                    </Button>
                  </Box>
                </Stack>
              ) : (
                <List>
                  {calendarConnections.map((connection) => (
                    <ListItem key={connection.id}>
                      <ListItemDecorator>
                        {connection.provider === 'google' ? <GoogleIcon /> : <GoogleIcon />}
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography level="title-sm">{connection.provider_display}</Typography>
                        <Typography level="body-xs">
                          {connection.is_active ? (
                            <Chip size="sm" color="success" startDecorator={<CheckCircleIcon />}>
                              Active
                            </Chip>
                          ) : (
                            <Chip size="sm" color="warning">
                              Inactive
                            </Chip>
                          )}
                          {connection.is_expired && (
                            <Chip size="sm" color="danger" sx={{ ml: 1 }}>
                              Token Expired
                            </Chip>
                          )}
                        </Typography>
                      </ListItemContent>
                      <IconButton
                        color="primary"
                        onClick={() => syncCalendarMutation.mutate(connection.id)}
                        loading={syncCalendarMutation.isPending}
                        sx={{ mr: 1 }}
                      >
                        <RefreshIcon />
                      </IconButton>
                      <IconButton
                        color="danger"
                        onClick={() => disconnectCalendarMutation.mutate(connection.id)}
                        loading={disconnectCalendarMutation.isPending}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        {/* Tab 2: Availability Settings */}
        <TabPanel value={1}>
          <Stack spacing={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography level="title-lg" sx={{ mb: 2 }}>
                  Working Hours
                </Typography>
                <Stack spacing={2}>
                  {DAYS_OF_WEEK.map(day => (
                    <Box key={day}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Switch
                          checked={workingHours[day]?.enabled || false}
                          onChange={() => handleToggleDay(day)}
                        />
                        <Typography sx={{ minWidth: 100 }}>{DAY_LABELS[day]}</Typography>
                        {workingHours[day]?.enabled && (
                          <>
                            <Input
                              type="time"
                              value={workingHours[day]?.start || '09:00'}
                              onChange={(e) => handleTimeChange(day, 'start', e.target.value)}
                              sx={{ width: 120 }}
                            />
                            <Typography>to</Typography>
                            <Input
                              type="time"
                              value={workingHours[day]?.end || '17:00'}
                              onChange={(e) => handleTimeChange(day, 'end', e.target.value)}
                              sx={{ width: 120 }}
                            />
                          </>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography level="title-lg" sx={{ mb: 2 }}>
                  Meeting Configuration
                </Typography>
                <Stack spacing={2}>
                  <FormControl>
                    <FormLabel>Default Meeting Duration</FormLabel>
                    <Select
                      value={meetingDuration}
                      onChange={(_, value) => setMeetingDuration(value as number)}
                    >
                      <Option value={15}>15 minutes</Option>
                      <Option value={30}>30 minutes</Option>
                      <Option value={45}>45 minutes</Option>
                      <Option value={60}>60 minutes</Option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Booking Window</FormLabel>
                    <Select
                      value={bookingWindow}
                      onChange={(_, value) => setBookingWindow(value as number)}
                    >
                      <Option value={7}>7 days</Option>
                      <Option value={14}>14 days</Option>
                      <Option value={30}>30 days</Option>
                      <Option value={60}>60 days</Option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Minimum Notice</FormLabel>
                    <Select
                      value={minimumNotice}
                      onChange={(_, value) => setMinimumNotice(value as number)}
                    >
                      <Option value={0}>No minimum</Option>
                      <Option value={1}>1 hour</Option>
                      <Option value={2}>2 hours</Option>
                      <Option value={4}>4 hours</Option>
                      <Option value={24}>24 hours</Option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Timezone</FormLabel>
                    <Select
                      value={timezone}
                      onChange={(_, value) => setTimezone(value as string)}
                    >
                      <Option value="America/New_York">Eastern Time</Option>
                      <Option value="America/Chicago">Central Time</Option>
                      <Option value="America/Denver">Mountain Time</Option>
                      <Option value="America/Los_Angeles">Pacific Time</Option>
                    </Select>
                  </FormControl>
                </Stack>
              </CardContent>
            </Card>

            <Stack spacing={2}>
              {!isEnabled && (
                <Alert color="warning" startDecorator={<WarningIcon />}>
                  Note: Enable your booking page in the "Booking Page" tab to preview availability and allow bookings.
                </Alert>
              )}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  onClick={handleSaveSettings}
                  loading={updateSettingsMutation.isPending}
                  size="lg"
                >
                  Save Availability Settings
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handlePreviewAvailability}
                  loading={previewLoading}
                  size="lg"
                >
                  Preview Available Slots
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </TabPanel>

        {/* Tab 3: Booking Page */}
        <TabPanel value={2}>
          <Stack spacing={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography level="title-lg" sx={{ mb: 2 }}>
                  Booking Page Details
                </Typography>
                <Stack spacing={2}>
                  <FormControl>
                    <FormLabel>Meeting Title</FormLabel>
                    <Input
                      value={meetingTitle}
                      onChange={(e) => setMeetingTitle(e.target.value)}
                      placeholder="Meeting with [Your Name]"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Meeting Location</FormLabel>
                    <Input
                      value={meetingLocation}
                      onChange={(e) => setMeetingLocation(e.target.value)}
                      placeholder="Google Meet, Zoom link, or phone number"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Welcome Message</FormLabel>
                    <Textarea
                      value={welcomeMessage}
                      onChange={(e) => setWelcomeMessage(e.target.value)}
                      placeholder="Add a message that invitees will see on your booking page"
                      minRows={3}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Custom URL Slug (Optional)</FormLabel>
                    <Input
                      value={customSlug}
                      onChange={(e) => setCustomSlug(e.target.value)}
                      placeholder="your-custom-slug"
                    />
                  </FormControl>

                  <Divider />

                  <FormControl orientation="horizontal">
                    <Box sx={{ flex: 1 }}>
                      <FormLabel>Enable Booking Page</FormLabel>
                      <Typography level="body-sm">
                        Allow people to book time with you via your booking link
                      </Typography>
                    </Box>
                    <Switch
                      checked={isEnabled}
                      onChange={(e) => setIsEnabled(e.target.checked)}
                    />
                  </FormControl>
                </Stack>
              </CardContent>
            </Card>

            {settings?.booking_url && isEnabled && (
              <Card variant="outlined" color="success">
                <CardContent>
                  <Typography level="title-lg" sx={{ mb: 2 }}>
                    Your Booking Link
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Input
                      value={settings.booking_url}
                      readOnly
                      sx={{ flex: 1 }}
                    />
                    <Button
                      startDecorator={<CopyIcon />}
                      onClick={handleCopyBookingLink}
                    >
                      Copy
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setEmbedCodeModalOpen(true)}
                    >
                      Get Embed Code
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            )}

            <Button
              onClick={handleSaveSettings}
              loading={updateSettingsMutation.isPending}
              size="lg"
            >
              Save Booking Page Settings
            </Button>
          </Stack>
        </TabPanel>
      </Tabs>

      {/* Embed Code Modal */}
      <Modal open={embedCodeModalOpen} onClose={() => setEmbedCodeModalOpen(false)}>
        <ModalDialog>
          <ModalClose />
          <DialogTitle>Embed Code</DialogTitle>
          <DialogContent>
            <Typography level="body-sm" sx={{ mb: 2 }}>
              Copy this code to embed your booking widget on your website:
            </Typography>
            <Textarea
              readOnly
              value={`<iframe src="${settings?.booking_url}" width="100%" height="600" frameborder="0"></iframe>`}
              minRows={4}
              sx={{ fontFamily: 'monospace', fontSize: 'xs' }}
            />
            <Button
              sx={{ mt: 2 }}
              onClick={() => {
                navigator.clipboard.writeText(`<iframe src="${settings?.booking_url}" width="100%" height="600" frameborder="0"></iframe>`);
                setAlertMessage({ type: 'success', message: 'Embed code copied!' });
                setEmbedCodeModalOpen(false);
              }}
            >
              Copy Embed Code
            </Button>
          </DialogContent>
        </ModalDialog>
      </Modal>

      {/* Preview Available Slots Modal */}
      <Modal open={previewModalOpen} onClose={() => setPreviewModalOpen(false)}>
        <ModalDialog sx={{ width: 600, minWidth: 0, maxWidth: 'calc(100vw - 32px)', maxHeight: '80vh', overflow: 'auto' }}>
          <ModalClose />
          <DialogTitle>Available Time Slots (Next 7 Days)</DialogTitle>
          <DialogContent>
            {previewLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : previewData && previewData.slots.length > 0 ? (
              <Stack spacing={2}>
                <Alert color="success">
                  Found {previewData.total_count} available time slots
                </Alert>
                {(() => {
                  // Group slots by day
                  interface TimeSlot { start_time: string; end_time: string; available: boolean }
                  const slotsByDay = previewData.slots.reduce((acc, slot) => {
                    const date = new Date(slot.start_time).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    });
                    if (!acc[date]) acc[date] = [];
                    acc[date].push(slot);
                    return acc;
                  }, {} as Record<string, TimeSlot[]>);

                  return Object.entries(slotsByDay).map(([date, slots]) => (
                    <Box key={date}>
                      <Typography level="title-md" sx={{ mb: 1, mt: 1 }}>
                        {date}
                      </Typography>
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                        gap: 1
                      }}>
                        {slots.map((slot, index) => (
                          <Chip
                            key={index}
                            color="success"
                            variant="soft"
                            sx={{
                              py: 1.5,
                              px: 2,
                              justifyContent: 'center',
                              fontWeight: 'md'
                            }}
                          >
                            {new Date(slot.start_time).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true,
                            })}
                          </Chip>
                        ))}
                      </Box>
                    </Box>
                  ));
                })()}
              </Stack>
            ) : previewError ? (
              <Stack spacing={2}>
                <Alert color="danger" startDecorator={<WarningIcon />}>
                  {(() => {
                    const err = previewError as { response?: { data?: { error?: string } } };
                    return err?.response?.data?.error === 'Booking page is not enabled' ? (
                      <>
                        <strong>Booking page is not enabled.</strong>
                        <Typography level="body-sm" sx={{ mt: 1 }}>
                          Please go to the "Booking Page" tab and enable your booking page, then save settings before previewing availability.
                        </Typography>
                      </>
                    ) : (
                      <>
                        <strong>Unable to preview availability.</strong>
                        <Typography level="body-sm" sx={{ mt: 1 }}>
                          {err?.response?.data?.error || 'An error occurred while fetching availability. Please ensure you have saved your settings and connected a calendar.'}
                        </Typography>
                      </>
                    );
                  })()}
                </Alert>
              </Stack>
            ) : (
              <Alert color="warning">
                No available slots found. Check your working hours and calendar connection.
              </Alert>
            )}
          </DialogContent>
        </ModalDialog>
      </Modal>
    </Box>
  );
};
